// src/components/UserContractView/components/EnhancedSignDialog.jsx

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';

import {
  Close as CloseIcon,
  Description as DocumentIcon,
  Link as LinkIcon,
  Launch as LaunchIcon,
  PictureAsPdf as PdfIcon,
  Cancel as DeclinedIcon,
} from '@mui/icons-material';

import { dateFromSignature } from './contractUtils';

const EnhancedSignDialog = ({ 
  open, 
  assignment,
  signature, 
  contractData, 
  loading: contractLoading, 
  onClose, 
  onAgree, 
  onDecline, 
  actionLoading, 
  readOnly,
  onNavigateToContract,
  buildPublicLink,
}) => {
  const [declineMode, setDeclineMode] = useState(false);
  const [reason, setReason] = useState('');
  const contentRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setDeclineMode(false);
      setReason('');
    }
  }, [open]);

  if (!assignment) return null;

  const template = assignment?.template || {};
  const isSimple = assignment?.is_simple;
  const assignmentId = assignment?.id;
  const publicLink = assignmentId ? buildPublicLink(assignmentId) : null;

  const processedBody = contractData?.processed_contract_body || 
                       contractData?.template?.contract_body || 
                       assignment.processed_contract_body || 
                       template.contract_body || 
                       '';

  const downloadPdf = async () => {
    if (!contentRef.current) return;
    const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
      import('html2canvas'),
      import('jspdf')
    ]);

    const canvas = await html2canvas(contentRef.current, { scale: 2 });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pageWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let position = 0;
    let heightLeft = imgHeight;

    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;
    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }
    pdf.save(`${template?.name || 'contract'}.pdf`);
  };

  const showActionButtons = !readOnly && signature?.status === 'pending';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        <Box display="flex" alignItems="center" gap={2}>
          <DocumentIcon color="primary" />
          {template?.name || 'Contract'}
          <Chip size="small" label="Preview" color="info" variant="outlined" />
          {publicLink && (
            <Tooltip title="Open public link">
              <IconButton
                size="small"
                sx={{ ml: 'auto' }}
                onClick={() => window.open(publicLink, '_blank', 'noopener')}
              >
                <LinkIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ backgroundColor: '#fafafa' }}>
        {assignment?.assignment_message && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2"><strong>Message:</strong> {assignment.assignment_message}</Typography>
          </Alert>
        )}
        {assignment?.due_date && signature?.status === 'pending' && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Due Date:</strong> {new Date(assignment.due_date).toLocaleString()}
            </Typography>
          </Alert>
        )}

        {contractLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>Loading contract...</Typography>
          </Box>
        ) : (
          <Box
            ref={contentRef}
            sx={{
              position: 'relative',
              maxHeight: 400,
              overflow: 'auto',
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              p: 2,
              mb: 3,
              backgroundColor: '#fff',
              fontFamily: 'Arial, sans-serif',
              lineHeight: 1.6
            }}
          >
            {processedBody ? (
              <div dangerouslySetInnerHTML={{ __html: processedBody }} />
            ) : (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <Typography variant="body2" color="text.secondary">
                  Contract content not available
                </Typography>
              </Box>
            )}

            {signature?.status && signature.status !== 'pending' && (
              <Box
                sx={{
                  position: 'absolute',
                  right: 16,
                  bottom: 16,
                  textAlign: 'right',
                  border: '1px solid #ccc',
                  p: 1.5,
                  borderRadius: 1,
                  background: '#fafafa',
                  maxWidth: 200
                }}
              >
                <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                  {signature.status === 'signed'
                    ? 'Digitally Signed'
                    : signature.status === 'agreed'
                    ? 'Agreed'
                    : 'Declined'}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block' }}>
                  {new Date(dateFromSignature(signature)).toLocaleString()}
                </Typography>
                {signature?.ip_address && (
                  <Typography variant="caption" sx={{ display: 'block' }}>
                    IP: {signature.ip_address}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        )}

        {!readOnly && signature?.status === 'pending' && declineMode && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" gutterBottom>
              Please provide a reason for declining:
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter your reason..."
              sx={{ mb: 2 }}
            />
          </Box>
        )}

        <Alert severity="info">
          <Typography variant="body2">
            {isSimple
              ? 'By clicking "I Agree", you confirm that you have read and agree to be bound by the terms above.'
              : 'By clicking "Sign Contract", you agree to be legally bound by the terms and conditions above. Your digital acceptance will be recorded.'}
          </Typography>
        </Alert>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button 
          onClick={onNavigateToContract} 
          startIcon={<LaunchIcon />} 
          disabled={actionLoading}
          variant="outlined"
        >
          Open Full Page
        </Button>
        <Button onClick={downloadPdf} startIcon={<PdfIcon />} disabled={actionLoading}>
          Download PDF
        </Button>
        <Button onClick={onClose} disabled={actionLoading}>Close</Button>

        {showActionButtons && (
          !declineMode ? (
            <>
              <Button
                variant="outlined"
                color="error"
                onClick={() => setDeclineMode(true)}
                disabled={actionLoading}
              >
                Decline
              </Button>
              <Button
                variant="contained"
                onClick={() => onAgree(signature, isSimple)}
                disabled={actionLoading}
                startIcon={actionLoading ? <CircularProgress size={16} /> : <DocumentIcon />}
              >
                {actionLoading ? 'Processing...' : isSimple ? 'I Agree' : 'Sign Contract'}
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              color="error"
              onClick={() => onDecline(signature, reason)}
              disabled={!reason.trim() || actionLoading}
              startIcon={actionLoading ? <CircularProgress size={16} /> : <DeclinedIcon />}
            >
              {actionLoading ? 'Submitting...' : 'Confirm Decline'}
            </Button>
          )
        )}
      </DialogActions>
    </Dialog>
  );
};

export default EnhancedSignDialog;